import { useEffect, useState } from "react";
import {
  Card,
  BlockStack,
  Badge,
  Banner,
  Button,
  InlineStack,
  Spinner,
  Text,
} from "@shopify/polaris";
import { api, ApiRequestError, isReauthError } from "../api";
import type { ConflictGroup, ConflictSummary, PromiseConflictScan } from "../types";
import { describePromiseKind } from "../lib/promise-source";
import { describeConflictScope, orderIdFromGid } from "../lib/conflict-format";

function groupKey(g: ConflictGroup): string {
  return `${g.source_a.kind}:${g.source_a.key ?? ""}|${g.source_b.kind}:${g.source_b.key ?? ""}`;
}

function sourceLabel(source: { kind: string; key: string | null }): string {
  const kind = describePromiseKind(source.kind);
  return source.key ? `${kind} — ${source.key}` : kind;
}

// Unlike the Delivery sources and Weight coverage cards, the summary loads on
// mount: it is one GROUP BY against D1 with no Shopify round trip, so gating it
// behind a button would only hide the feature. The scan below does walk orders, so
// that keeps the button.
export default function PromiseConflictsCard(): JSX.Element {
  const [summary, setSummary] = useState<ConflictSummary | null>(null);
  const [scan, setScan] = useState<PromiseConflictScan | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadSummary(): Promise<void> {
    try {
      setSummary(await api.get<ConflictSummary>("/promise/conflicts/summary"));
    } catch (err) {
      // The app shell already renders the Reconnect banner for this one, so a
      // second copy of the same message here would be noise.
      if (isReauthError(err)) return;
      setError(
        err instanceof ApiRequestError ? err.body.message : "Could not load promise conflicts"
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadSummary();
  }, []);

  async function runScan(): Promise<void> {
    setScanning(true);
    setError(null);
    try {
      setScan(
        await api.get<PromiseConflictScan>(`/promise/conflicts/scan${scan ? "?refresh=1" : ""}`)
      );
      await loadSummary();
    } catch (err) {
      if (!isReauthError(err)) {
        setError(err instanceof ApiRequestError ? err.body.message : "Could not scan orders");
      }
    } finally {
      setScanning(false);
    }
  }

  async function dismiss(group: ConflictGroup): Promise<void> {
    setDismissing(groupKey(group));
    setError(null);
    try {
      await api.post("/promise/conflicts/dismiss-group", {
        source_a_kind: group.source_a.kind,
        source_a_key: group.source_a.key,
        source_b_kind: group.source_b.kind,
        source_b_key: group.source_b.key,
        days: summary?.window_days,
      });
      await loadSummary();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.message : "Could not dismiss");
    } finally {
      setDismissing(null);
    }
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Promise conflicts
        </Text>
        <Text as="p" tone="subdued">
          When an order carries two delivery dates that disagree, ShipPromise records both. The
          deadline it actually used is the first one in its priority order — this panel is here so a
          disagreement points you at the field to fix in Shopify. Grouped by which two sources
          disagreed, over the last {summary?.window_days ?? 30} days.
        </Text>

        {error && <Banner tone="critical">{error}</Banner>}

        <InlineStack gap="200" blockAlign="center">
          <Button onClick={() => void runScan()} loading={scanning}>
            {scan ? "Rescan orders" : "Scan recent orders"}
          </Button>
          {scan && (
            <Text as="span" tone="subdued" variant="bodySm">
              {scan.order_count} order{scan.order_count === 1 ? "" : "s"} scanned
              {scan.truncated ? " (most recent only)" : ""} · {scan.conflicts_found} conflict
              {scan.conflicts_found === 1 ? "" : "s"} found
            </Text>
          )}
        </InlineStack>

        {scan && (
          <Text as="p" tone="subdued" variant="bodySm">
            A scan checks {scan.kinds_covered.map(describePromiseKind).join(", ")}. Per-line dates
            are only read when an order is actually shipped, so those appear here as new orders come
            through rather than from a scan.
          </Text>
        )}

        {loading && <Spinner accessibilityLabel="Loading promise conflicts" size="small" />}

        {!loading && summary && summary.groups.length === 0 && (
          <Banner tone="success">
            No disagreements in the last {summary.window_days} days. Every order that carried a
            delivery date carried only one.
          </Banner>
        )}

        {!loading &&
          summary?.groups.map((group) => (
            <BlockStack key={groupKey(group)} gap="150">
              <InlineStack gap="150" blockAlign="center" wrap>
                <Badge tone={group.breach_count > 0 ? "critical" : undefined}>
                  {sourceLabel(group.source_a)}
                </Badge>
                <Text as="span" tone="subdued">
                  vs
                </Text>
                <Badge>{sourceLabel(group.source_b)}</Badge>
              </InlineStack>

              <Text as="p" tone="subdued" variant="bodySm">
                {describeConflictScope(group)}
              </Text>

              <InlineStack gap="200" blockAlign="center" wrap>
                {group.example_order_gids.map((gid) => {
                  const id = orderIdFromGid(gid);
                  // A GID we cannot parse renders nothing rather than a dead link.
                  if (!id) return null;
                  return (
                    // The shopify://admin protocol with target="_top" is what
                    // navigates the admin around this app's iframe. A bare
                    // /admin/orders/... would navigate the iframe itself and strand
                    // the merchant inside a broken embedded view.
                    <a key={gid} href={`shopify://admin/orders/${id}`} target="_top">
                      Order {id}
                    </a>
                  );
                })}
                <Button
                  size="slim"
                  variant="plain"
                  loading={dismissing === groupKey(group)}
                  onClick={() => void dismiss(group)}
                >
                  Dismiss
                </Button>
              </InlineStack>
            </BlockStack>
          ))}

        {!loading && summary && summary.groups.length > 0 && (
          <Text as="p" tone="subdued" variant="bodySm">
            Dismissing hides a pair you have decided is expected. If a new order trips the same pair
            it comes back with a count of one — that is a new occurrence, not the old one returning.
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}
